import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Clock,
  CheckCircle,
  Calendar,
  DollarSign,
  Building2,
  HardHat,
  User,
  FileText,
  TrendingUp,
  Plus,
  Search,
  Filter,
  RefreshCw,
  CreditCard,
  Send,
} from 'lucide-react';
import {
  GlassCard,
  GlassButton,
  GlassInput,
  GlassSelect,
  GlassTable,
  GlassModal,
  GlassTextarea,
  FadeIn,
} from '../../components/ui';
import { api } from '../../hooks/useApi';

const BOOKS = {
  imobiliaria: { name: 'Imobiliaria', color: '#22c55e', icon: Building2 },
  construtora: { name: 'Construtora', color: '#3b82f6', icon: HardHat },
  pessoal: { name: 'Pessoal', color: '#8b5cf6', icon: User },
  geral: { name: 'Geral', color: '#94a3b8', icon: FileText },
};

const INITIAL_FORM = {
  valor: '',
  data_vencimento: new Date().toISOString().slice(0, 10),
  descricao: '',
  pessoa_nome: '',
  pessoa_tipo: 'cliente',
  numero_documento: '',
  conta_id: '',
  categoria_id: '',
  obra_id: '',
  imovel_id: '',
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(`${value}T00:00:00`).toLocaleDateString('pt-BR');
};

const daysUntil = (value) => {
  if (!value) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${value}T00:00:00`);
  return Math.ceil((due - today) / 86400000);
};

const normalizeText = (value) => String(value || '').toLowerCase();

const toOptions = (items, labelKeys = ['nome']) => (
  items || []
).map((item) => ({
  value: item.id,
  label: labelKeys.map((key) => item[key]).filter(Boolean).join(' - ') || item.id,
}));

export const FinanceiroContasReceber = () => {
  const [contas, setContas] = useState([]);
  const [summary, setSummary] = useState({ vencidas: 0, aVencer: 0, total: 0 });
  const [filters, setFilters] = useState({ book: 'all', status: 'all', search: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [receivingId, setReceivingId] = useState(null);
  const [legalSendingId, setLegalSendingId] = useState(null);
  const [options, setOptions] = useState({ contas: [], categorias: [], obras: [], imoveis: [] });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadContas = useCallback(async () => {
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const response = await api.get('/contabilidade/contas-a-receber');
      const vencidas = (response.data.vencidas?.items || []).map((item) => ({ ...item, derived_status: 'atrasado' }));
      const aVencer = (response.data.a_vencer?.items || []).map((item) => ({ ...item, derived_status: 'pendente' }));
      setContas([...vencidas, ...aVencer]);
      setSummary({
        vencidas: vencidas.reduce((acc, item) => acc + Number(item.valor || 0), 0),
        aVencer: aVencer.reduce((acc, item) => acc + Number(item.valor || 0), 0),
        total: Number(response.data.total || 0),
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar contas a receber.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContas();
  }, [loadContas]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [contasRes, categoriasRes, obrasRes, imoveisRes] = await Promise.all([
          api.get('/contabilidade/contas-bancarias'),
          api.get('/contabilidade/plano-contas', { params: { tipo: 'receita' } }),
          api.get('/construtora/obras', { params: { per_page: 200 } }),
          api.get('/imobiliaria/imoveis', { params: { per_page: 200 } }),
        ]);
        setOptions({
          contas: toOptions(contasRes.data.items, ['nome', 'banco']),
          categorias: toOptions(categoriasRes.data.items, ['codigo', 'nome']),
          obras: toOptions(obrasRes.data.items, ['nome']),
          imoveis: toOptions(imoveisRes.data.items, ['codigo', 'titulo']),
        });
      } catch (err) {
        console.warn('Erro ao carregar opcoes de contas a receber', err);
      }
    };
    loadOptions();
  }, []);

  const proximos7 = useMemo(() => {
    return contas
      .filter((item) => {
        const days = daysUntil(item.data_vencimento);
        return days >= 0 && days <= 7;
      })
      .reduce((acc, item) => acc + Number(item.valor || 0), 0);
  }, [contas]);

  const inadimplencia = summary.total > 0 ? ((summary.vencidas / summary.total) * 100).toFixed(1) : '0.0';

  const filteredData = useMemo(() => {
    return contas.filter((item) => {
      const book = item.book || 'geral';
      if (filters.book !== 'all' && book !== filters.book) return false;
      if (filters.status !== 'all' && item.derived_status !== filters.status) return false;
      if (filters.search) {
        const haystack = [
          item.descricao,
          item.pessoa_nome,
          item.numero_documento,
          item.categoria,
          item.conta,
        ].map(normalizeText).join(' ');
        if (!haystack.includes(normalizeText(filters.search))) return false;
      }
      return true;
    });
  }, [contas, filters]);

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleCreate = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        tipo: 'receita',
        status: 'pendente',
        valor: Number(String(form.valor).replace(',', '.')),
      };
      if (payload.obra_id) payload.imovel_id = '';
      if (payload.imovel_id) payload.obra_id = '';
      await api.post('/contabilidade/movimentacoes', payload);
      setIsModalOpen(false);
      setForm(INITIAL_FORM);
      await loadContas();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao criar conta a receber.');
    } finally {
      setSaving(false);
    }
  };

  const handleReceive = async (row) => {
    setReceivingId(row.id);
    setError('');
    try {
      await api.post(`/contabilidade/movimentacoes/${row.id}/pagar`, {
        data_pagamento: new Date().toISOString().slice(0, 10),
      });
      await loadContas();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao marcar recebimento.');
    } finally {
      setReceivingId(null);
    }
  };

  const handleSendToLegal = async (row) => {
    setLegalSendingId(row.id);
    setError('');
    setNotice('');
    try {
      const response = await api.post('/juridico/cobrancas/from-financeiro', {
        movimentacao_id: row.id,
        enviar_escritorio: true,
        complemento: 'Enviado pela tela de Contas a Receber do Financeiro.',
      });
      const emailStatus = response.data?.email?.status;
      setNotice(emailStatus === 'enviado'
        ? 'Cobrança enviada ao Jurídico e e-mail disparado ao escritório.'
        : 'Cobrança enviada ao Jurídico; briefing ficou salvo na fila de e-mail.');
      await loadContas();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Erro ao enviar cobrança ao Jurídico.');
    } finally {
      setLegalSendingId(null);
    }
  };

  const columns = [
    {
      key: 'cliente',
      title: 'Cliente',
      accessor: 'pessoa_nome',
      render: (row) => (
        <div>
          <p className="text-white/90">{row.pessoa_nome || 'Cliente nao informado'}</p>
          <p className="text-xs text-white/50">{row.descricao || `Venc: ${formatDate(row.data_vencimento)}`}</p>
        </div>
      ),
    },
    {
      key: 'book',
      title: 'Book',
      accessor: 'book',
      render: (row) => {
        const book = BOOKS[row.book] || BOOKS.geral;
        return (
          <div className="flex items-center gap-2">
            {React.createElement(book.icon, {
              className: 'w-4 h-4',
              style: { color: book.color },
            })}
            <span style={{ color: book.color }}>{book.name}</span>
          </div>
        );
      },
    },
    {
      key: 'valor',
      title: 'Valor',
      accessor: 'valor',
      render: (row) => <span className="text-green-400 font-medium">{formatCurrency(row.valor)}</span>,
    },
    {
      key: 'status',
      title: 'Status',
      accessor: 'derived_status',
      render: (row) => {
        const overdue = row.derived_status === 'atrasado';
        const Icon = overdue ? AlertTriangle : Clock;
        return (
          <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
            overdue ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
          }`}>
            <Icon className="w-3 h-3" />
            {overdue ? 'Atrasado' : 'Pendente'}
          </span>
        );
      },
    },
    {
      key: 'alerta',
      title: 'Alerta',
      render: (row) => {
        const days = daysUntil(row.data_vencimento);
        if (days < 0) {
          return (
            <span className="flex items-center gap-1 text-red-400 text-xs">
              <AlertTriangle className="w-4 h-4" />
              {Math.abs(days)} dias atrasado
            </span>
          );
        }
        if (days <= 7) {
          return (
            <span className="flex items-center gap-1 text-yellow-400 text-xs">
              <Calendar className="w-4 h-4" />
              Vence em {days} dias
            </span>
          );
        }
        return <span className="text-white/40 text-xs">-</span>;
      },
    },
    {
      key: 'acoes',
      title: 'Acoes',
      render: (row) => (
        <div className="flex flex-wrap gap-2">
          {row.derived_status === 'atrasado' && (
            <GlassButton
              size="xs"
              variant="warning"
              icon={Send}
              loading={legalSendingId === row.id}
              onClick={(event) => {
                event.stopPropagation();
                handleSendToLegal(row);
              }}
            >
              Jurídico
            </GlassButton>
          )}
          <GlassButton
            size="xs"
            variant="success"
            icon={CreditCard}
            loading={receivingId === row.id}
            onClick={(event) => {
              event.stopPropagation();
              handleReceive(row);
            }}
          >
            Receber
          </GlassButton>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Contas a Receber</h1>
          <p className="text-white/50">
            {loading ? 'Carregando recebiveis reais...' : `${filteredData.length} recebiveis em aberto`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <GlassButton variant="secondary" icon={RefreshCw} onClick={loadContas} loading={loading}>
            Atualizar
          </GlassButton>
          <GlassButton icon={Plus} onClick={() => setIsModalOpen(true)}>
            Nova conta
          </GlassButton>
        </div>
      </div>

      {error && (
        <GlassCard className="p-4 border-red-500/30 bg-red-500/10" hover={false}>
          <p className="text-sm text-red-300">{error}</p>
        </GlassCard>
      )}
      {notice && (
        <GlassCard className="p-4 border-emerald-500/30 bg-emerald-500/10" hover={false}>
          <p className="text-sm text-emerald-200">{notice}</p>
        </GlassCard>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">A receber</p>
              <p className="text-2xl font-bold text-green-400">{formatCurrency(summary.aVencer)}</p>
            </div>
            <div className="p-3 rounded-xl bg-green-500/20">
              <DollarSign className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6 border-red-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Vencido</p>
              <p className="text-2xl font-bold text-red-400">{formatCurrency(summary.vencidas)}</p>
            </div>
            <div className="p-3 rounded-xl bg-red-500/20">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Taxa vencida</p>
              <p className="text-2xl font-bold text-orange-400">{inadimplencia}%</p>
            </div>
            <div className="p-3 rounded-xl bg-orange-500/20">
              <TrendingUp className="w-6 h-6 text-orange-400" />
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Proximos 7 dias</p>
              <p className="text-2xl font-bold text-white/90">{formatCurrency(proximos7)}</p>
            </div>
            <div className="p-3 rounded-xl bg-accent-primary/20">
              <Calendar className="w-6 h-6 text-accent-primary" />
            </div>
          </div>
        </GlassCard>
      </div>

      <FadeIn>
        <GlassCard className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <GlassInput
              icon={Search}
              placeholder="Buscar cliente ou descricao..."
              className="max-w-xs"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />

            <GlassSelect
              value={filters.book}
              onChange={(e) => setFilters({ ...filters, book: e.target.value })}
              placeholder=""
              className="min-w-[170px]"
              options={[
                { value: 'all', label: 'Todos os books' },
                { value: 'imobiliaria', label: 'Imobiliaria' },
                { value: 'construtora', label: 'Construtora' },
                { value: 'pessoal', label: 'Pessoal' },
                { value: 'geral', label: 'Geral' },
              ]}
            />

            <GlassSelect
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              placeholder=""
              className="min-w-[150px]"
              options={[
                { value: 'all', label: 'Todos os status' },
                { value: 'pendente', label: 'Pendente' },
                { value: 'atrasado', label: 'Atrasado' },
              ]}
            />

            <GlassButton variant="ghost" icon={Filter}>
              Filtros
            </GlassButton>
          </div>
        </GlassCard>
      </FadeIn>

      <FadeIn delay={0.2}>
        <GlassCard>
          <GlassTable
            columns={columns}
            data={filteredData}
            searchable={false}
            emptyMessage={loading ? 'Carregando contas...' : 'Nenhuma conta a receber em aberto'}
          />
        </GlassCard>
      </FadeIn>

      <GlassModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Nova Conta a Receber"
        icon={Plus}
        size="lg"
        footer={
          <>
            <GlassButton variant="ghost" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </GlassButton>
            <GlassButton onClick={handleCreate} loading={saving}>
              Salvar
            </GlassButton>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassInput
            label="Valor"
            type="number"
            min="0"
            step="0.01"
            value={form.valor}
            onChange={(e) => handleChange('valor', e.target.value)}
            placeholder="0,00"
          />
          <GlassInput
            label="Vencimento"
            type="date"
            value={form.data_vencimento}
            onChange={(e) => handleChange('data_vencimento', e.target.value)}
          />
          <div className="md:col-span-2">
            <GlassTextarea
              label="Descricao"
              rows={3}
              value={form.descricao}
              onChange={(e) => handleChange('descricao', e.target.value)}
              placeholder="Ex.: parcela, aluguel, prestacao de servico..."
            />
          </div>
          <GlassSelect
            label="Conta bancaria"
            value={form.conta_id}
            onChange={(e) => handleChange('conta_id', e.target.value)}
            placeholder="Sem conta"
            options={options.contas}
          />
          <GlassSelect
            label="Plano de contas"
            value={form.categoria_id}
            onChange={(e) => handleChange('categoria_id', e.target.value)}
            placeholder="Sem categoria"
            options={options.categorias}
          />
          <GlassInput
            label="Cliente"
            value={form.pessoa_nome}
            onChange={(e) => handleChange('pessoa_nome', e.target.value)}
            placeholder="Nome do cliente"
          />
          <GlassSelect
            label="Tipo de pessoa"
            value={form.pessoa_tipo}
            onChange={(e) => handleChange('pessoa_tipo', e.target.value)}
            placeholder=""
            options={[
              { value: 'cliente', label: 'Cliente' },
              { value: 'fornecedor', label: 'Fornecedor' },
              { value: 'funcionario', label: 'Funcionario' },
              { value: 'colaborador', label: 'Colaborador' },
            ]}
          />
          <GlassInput
            label="Documento"
            value={form.numero_documento}
            onChange={(e) => handleChange('numero_documento', e.target.value)}
            placeholder="Contrato, recibo, NF..."
          />
          <GlassSelect
            label="Obra"
            value={form.obra_id}
            onChange={(e) => setForm((current) => ({ ...current, obra_id: e.target.value, imovel_id: e.target.value ? '' : current.imovel_id }))}
            placeholder="Sem obra"
            options={options.obras}
          />
          <GlassSelect
            label="Imovel"
            value={form.imovel_id}
            onChange={(e) => setForm((current) => ({ ...current, imovel_id: e.target.value, obra_id: e.target.value ? '' : current.obra_id }))}
            placeholder="Sem imovel"
            options={options.imoveis}
          />
        </div>
      </GlassModal>
    </div>
  );
};

export default FinanceiroContasReceber;
